import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	AlertCircle,
	Building2,
	Check,
	ChevronDown,
	ChevronUp,
	Gift,
	KeyRound,
	MoreVertical,
	Pencil,
	Plus,
	Search,
	Ticket,
	Trash2,
	Users
} from 'lucide-react';
import type { CustomerToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomersList, useCustomerMutations, useImpersonateCustomer } from '~/hooks/use-customers';
import { useCustomerCategoriesList } from '~/hooks/use-customer-categories';
import type { SortDir } from '~/lib/grid/sort';
import { CustomerEditorSheet } from './customer-editor-sheet';
import { CustomerBranchesDialog } from './customer-branches-dialog';
import { CustomerCouponsDialog } from './customer-coupons-dialog';
import { CustomerReferralsDialog } from './customer-referrals-dialog';
import { CustomerGroupOverridesDialog } from './customer-group-overrides-dialog';

const PAGE_SIZES = [25, 50, 100];

const Bool = ({ on }: { on?: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

type SortKey = 'Email' | 'FirstName' | 'LastName' | 'CreateDate';

/** Customers list (Πελάτες) — server-paged grid with editor + per-row sub-views and impersonation. */
export function CustomersGridView() {
	const { t } = useTranslation();

	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sortKey, setSortKey] = useState<SortKey | null>('CreateDate');
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	// Debounce the search box → server filter.
	useEffect(() => {
		const handle = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 350);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const { data, isLoading, isError, refetch, isFetching } = useCustomersList({
		skip: page * pageSize,
		take: pageSize,
		search,
		sortSelector: sortKey,
		sortDir
	});
	const { remove } = useCustomerMutations();
	const impersonate = useImpersonateCustomer();
	const { data: categories } = useCustomerCategoriesList();
	const categoryName = useMemo(() => new Map((categories ?? []).map(c => [c.id, c.nm])), [categories]);

	const [editorOpen, setEditorOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [deleteRow, setDeleteRow] = useState<CustomerToken | null>(null);
	const [branchesFor, setBranchesFor] = useState<CustomerToken | null>(null);
	const [couponsFor, setCouponsFor] = useState<CustomerToken | null>(null);
	const [referralsFor, setReferralsFor] = useState<CustomerToken | null>(null);
	const [overridesFor, setOverridesFor] = useState<CustomerToken | null>(null);

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			setSortDir('asc');
		}
		setPage(0);
	};

	const SortHeader = ({ col, label, className }: { col: SortKey; label: string; className?: string }) => (
		<button type="button" onClick={() => toggleSort(col)} className={cn('inline-flex items-center gap-1 font-medium', className)}>
			{label}
			{sortKey === col && (sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
		</button>
	);

	const openEditor = (id: number | null) => {
		setEditingId(id);
		setEditorOpen(true);
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const doImpersonate = (c: CustomerToken) => {
		if (!c.id) return;
		impersonate.mutate(c.id, {
			onSuccess: token => {
				if (token.redirect_url) globalThis.open(token.redirect_url, '_blank', 'noopener');
				else toast.error(serverMessage(null));
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const fullName = (c: CustomerToken) => [c.fn, c.ln].filter(Boolean).join(' ').trim();

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('customers.list.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('customers.list.subtitle', { count: total })}</p>
				</div>
				<Button onClick={() => openEditor(null)}>
					<Plus size={16} /> {t('customers.list.add')}
				</Button>
			</div>

			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						placeholder={t('customers.list.searchPlaceholder')}
						className="pl-8"
					/>
				</div>
			</div>

			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<Users size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className={cn('w-full border-collapse text-sm', isFetching && 'opacity-60')}>
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className="px-3 py-2 text-left">
									<SortHeader col="Email" label={t('customers.list.columns.email')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="FirstName" label={t('customers.list.columns.name')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="LastName" label={t('customers.list.columns.surname')} />
								</th>
								<th className="px-3 py-2 text-left font-medium">{t('customers.list.columns.category')}</th>
								<th className="px-3 py-2 text-center font-medium">{t('customers.list.columns.active')}</th>
								<th className="px-3 py-2 text-center font-medium">{t('customers.list.columns.confirmed')}</th>
								<th className="px-2 py-2" style={{ width: 48 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map(c => (
								<tr key={c.id} className="group border-b border-border/60 hover:bg-muted/50">
									<td className="px-3 py-2">
										<button
											type="button"
											onClick={() => openEditor(c.id ?? null)}
											className="truncate text-left font-medium text-foreground hover:underline">
											{c.eml}
										</button>
									</td>
									<td className="px-3 py-2 text-foreground-muted">{c.fn || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{c.ln || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{(c.ccids ?? []).map(id => categoryName.get(id) ?? id).join(', ') || '—'}</td>
									<td className="px-3 py-2 text-center">
										<Bool on={c.act} />
									</td>
									<td className="px-3 py-2 text-center">
										<Bool on={c.cfm} />
									</td>
									<td className="px-2 py-1 text-center">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('common.sections')}>
													<MoreVertical size={15} />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="w-52">
												<DropdownMenuItem onSelect={() => openEditor(c.id ?? null)}>
													<Pencil size={14} /> {t('common.edit')}
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onSelect={() => setBranchesFor(c)}>
													<Building2 size={14} /> {t('customers.list.actions.branches')}
												</DropdownMenuItem>
												<DropdownMenuItem onSelect={() => setCouponsFor(c)}>
													<Ticket size={14} /> {t('customers.list.actions.coupons')}
												</DropdownMenuItem>
												<DropdownMenuItem onSelect={() => setReferralsFor(c)}>
													<Gift size={14} /> {t('customers.list.actions.referrals')}
												</DropdownMenuItem>
												<DropdownMenuItem onSelect={() => setOverridesFor(c)}>
													<Users size={14} /> {t('customers.list.actions.groupOverrides')}
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onSelect={() => doImpersonate(c)}>
													<KeyRound size={14} /> {t('customers.list.actions.impersonate')}
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem variant="destructive" onSelect={() => setDeleteRow(c)}>
													<Trash2 size={14} /> {t('common.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Pagination */}
			<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<select
						value={pageSize}
						onChange={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						className="h-8 rounded-md border border-border bg-surface px-2">
						{PAGE_SIZES.map(s => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>
				<div className="flex items-center gap-2">
					<span>{t('customers.list.pageInfo', { page: page + 1, pages: pageCount, total })}</span>
					<Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
						{t('common.previous')}
					</Button>
					<Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
						{t('common.next')}
					</Button>
				</div>
			</div>

			<CustomerEditorSheet open={editorOpen} onOpenChange={setEditorOpen} customerId={editingId} />

			<CustomerBranchesDialog
				open={Boolean(branchesFor)}
				onOpenChange={open => !open && setBranchesFor(null)}
				customerId={branchesFor?.id ?? null}
				customerName={branchesFor ? fullName(branchesFor) || branchesFor.eml : undefined}
			/>
			<CustomerCouponsDialog
				open={Boolean(couponsFor)}
				onOpenChange={open => !open && setCouponsFor(null)}
				customerId={couponsFor?.id ?? null}
				customerName={couponsFor ? fullName(couponsFor) || couponsFor.eml : undefined}
			/>
			<CustomerReferralsDialog
				open={Boolean(referralsFor)}
				onOpenChange={open => !open && setReferralsFor(null)}
				customerId={referralsFor?.id ?? null}
				customerName={referralsFor ? fullName(referralsFor) || referralsFor.eml : undefined}
				onOpenCustomer={id => {
					setReferralsFor(null);
					openEditor(id);
				}}
			/>
			<CustomerGroupOverridesDialog
				open={Boolean(overridesFor)}
				onOpenChange={open => !open && setOverridesFor(null)}
				customerId={overridesFor?.id ?? null}
				customerName={overridesFor ? fullName(overridesFor) || overridesFor.eml : undefined}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.list.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('customers.list.deleteBody', { name: deleteRow?.eml || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
